import { useEffect, useState } from "react";
import { authedFetch } from "../lib/api";

// The bridge to the amc-seat-extension Chrome extension.
//
// The extension's content script has no credential of its own -- Firebase Auth
// is header-based, so a `fetch('/api/...')` from `sync.js` gets a 401. So the
// page, which does have a token, fetches the payloads and re-emits them as
// hidden divs that the content script reads out of the DOM. The div is not a
// Streamlit artifact; it is a bridge between two execution contexts, and it
// survives the framework.
//
// THE IDS AND THE JSON SHAPES ARE A CONTRACT with a separate repo. They live in
// `server/services/extension_contract.py` and are pinned on both sides
// (`test_extension_contract.py` here, `tests/app_contract.test.js` there).
// Renaming either half produces no error anywhere -- the extension simply stops
// finding data and seat capture silently stops working. The server sends these
// through verbatim; nothing in this file reshapes them.

const PLANNER_ELEMENT_ID = "amc-planner-data";
const SEAT_HISTORY_ELEMENT_ID = "amc-seat-history-data";

type SyncPayload = { planner: unknown[]; seat_history: unknown[] };

/** Hidden JSON for a content script. `display:none` so it never paints. */
function DataDiv({ id, data }: { id: string; data: unknown[] }) {
  // An empty payload renders nothing at all. `sync.js` returns early on an
  // empty array anyway, but its `hasSyncData()` gate is an id lookup, so an
  // empty div would schedule a sync pass that has nothing to do.
  if (data.length === 0) return null;
  return (
    <div id={id} style={{ display: "none" }}>
      {JSON.stringify(data)}
    </div>
  );
}

/**
 * Fetches the extension payloads and renders them into the page.
 *
 * Mounted on the planner route only, matching where the Streamlit app emitted
 * them. The third div, `#amc-resolver-session-data`, belongs to the Movie
 * Finder at `/` and lands with the resolved-link write-back; `sync.js` gates
 * that write-back on the div existing, so leaving it out here is inert rather
 * than half-wired.
 *
 * Every failure is silent. The extension is an enhancement to a page whose job
 * is showing plans, and the Streamlit version was a bare `except: pass` for the
 * same reason.
 */
export function ExtensionBridge() {
  const [payload, setPayload] = useState<SyncPayload | null>(null);

  useEffect(() => {
    let active = true;
    authedFetch("/api/extension/sync")
      .then((res) => (res.ok ? res.json() : null))
      .then((body: SyncPayload | null) => {
        if (active && body) setPayload(body);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!payload) return null;

  return (
    <>
      <DataDiv id={PLANNER_ELEMENT_ID} data={payload.planner ?? []} />
      <DataDiv id={SEAT_HISTORY_ELEMENT_ID} data={payload.seat_history ?? []} />
    </>
  );
}
